import { createCollection } from '@rocketlane/state-db';
import { ProjectAPI } from 'api/project';
/**
 * The single source of truth for the **ProjectPhase** entity.
 *
 * Replaces two duplicate homes: `project.projectPhases[]` (inside
 * `projectCollection`) and `planBoardPhaseCollection.phase` (inside
 * `collections/plan-board/`), both kept in sync by hand today via
 * `patchProjectPhaseInStores` + `phase-progress-reconciler.ts`. Those become
 * derived reads off this collection. See `docs/state-db-migration-phase.md`.
 *
 * Migration scope is phase-bean CRUD + reorder only — phase dependencies
 * (`PhaseAPI.createDependentPhase` et al.) and `PlanLane` entity-order
 * pagination stay where they are.
 */ /**
 * `ProjectPhase.projectId` is optional and duplicated as `project: Project`
 * (sometimes a stub). Never assume either is present — resolve through this
 * helper, and stamp `projectId` at ingest time when the caller already knows
 * it (see `fetchers.ts`).
 */ export const getPhaseProjectId = (phase)=>{
    var _phase_projectId;
    var _phase_project;
    return (_phase_projectId = phase.projectId) !== null && _phase_projectId !== void 0 ? _phase_projectId : (_phase_project = phase.project) === null || _phase_project === void 0 ? void 0 : _phase_project.projectId;
};
/**
 * Every phase mutation (update/delete) needs the owning project's id for the
 * nested-resource endpoint (`/projects/{projectId}/phases/{phaseId}`), but the
 * bean itself doesn't reliably carry it. Prefer `ctx.original` (the phase
 * already in the collection); fall back to metadata for the rare case a
 * caller mutates a phase that was never pushed with a stamped `projectId`.
 */ const resolveProjectId = (ctx)=>{
    var _ref;
    var _ctx_metadata;
    const projectId = (_ref = ctx.original && getPhaseProjectId(ctx.original)) !== null && _ref !== void 0 ? _ref : (_ctx_metadata = ctx.metadata) === null || _ctx_metadata === void 0 ? void 0 : _ctx_metadata.projectId;
    if (projectId == null) {
        throw new Error(`phase mutation: cannot resolve projectId for phase ${String(ctx.id)}`);
    }
    return projectId;
};
const routeUpdate = async (ctx)=>{
    var _ctx_changes;
    const projectId = resolveProjectId(ctx);
    return ProjectAPI.updateProjectPhase(projectId, Number(ctx.id), (_ctx_changes = ctx.changes) !== null && _ctx_changes !== void 0 ? _ctx_changes : {});
};
export const phaseCollection = createCollection({
    id: 'project-phases',
    getKey: (phase)=>phase.projectPhaseId,
    indexes: {
        // `-1` sentinel rather than `undefined`/`null`: an unresolvable projectId
        // is itself a bug worth surfacing via a visible bucket, not a silently
        // dropped phase.
        byProject: (phase)=>{
            var _getPhaseProjectId;
            return (_getPhaseProjectId = getPhaseProjectId(phase)) !== null && _getPhaseProjectId !== void 0 ? _getPhaseProjectId : -1;
        }
    },
    // No monotonic version on the bean — always-replace, same as projectCollection.
    isNewer: ()=>true,
    onUpdate: routeUpdate,
    onDelete: async (ctx)=>{
        const projectId = resolveProjectId(ctx);
        await ProjectAPI.deleteProjectPhase(projectId, Number(ctx.id));
    }
});
/** Imperative read of one phase from the collection. */ export const getPhaseFromCollection = (phaseId)=>phaseCollection.get(phaseId);
/** Imperative read of every phase currently loaded for a project. */ export const getPhasesForProjectFromCollection = (projectId)=>phaseCollection.getByIndex('byProject', projectId);
